import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from 'react-router-dom';
import db from "./Firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import Moment from 'moment';
import { extendMoment } from 'moment-range';

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function AtosCertificateList() {
    const moment = extendMoment(Moment);
    const [certificates, setCertificates] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCentre, setSelectedCentre] = useState("");
    const [selectedCourse, setSelectedCourse] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const navigate = useNavigate();
    const [email, setEmail] = useState("");

    const tableRef = useRef(null); // for Excel export

    useEffect(() => {
        const storedUsername = localStorage.getItem("email");
        const storedPassword = localStorage.getItem("password");
        const storedRemember = localStorage.getItem("remember");

        setEmail(storedUsername);

        if (storedRemember === "false" || !storedUsername || !storedPassword) {
            navigate('/signin');
        }
    }, [navigate]);

    useEffect(() => {
        const q = query(collection(db, "atos_certificates_data"), orderBy("timestamp", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setCertificates(snapshot.docs.map(doc => ({
                id: doc.id,
                data: doc.data(),
            })));
        });
        return () => unsubscribe();
    }, []);

    // get unique centres & courses
    const uniqueCentres = [...new Set(certificates.map(({ data }) => data.centre))].filter(Boolean);
    const uniqueCourses = [...new Set(certificates.map(({ data }) => data.course))].filter(Boolean);

    // Filtering
    const filteredCertificates = certificates.filter(({ data }) => {
        const searchableString = `
          ${data.candidateId || ""}
          ${data.candidateName || ""}
          ${data.centre || ""}
          ${data.certificateNo || ""}
          ${data.course || ""}
          ${data.start || ""}
          ${data.end || ""}
          ${data.issued || ""}
        `.toLowerCase();

        const matchesSearch = searchableString.includes(searchTerm.toLowerCase());
        const matchesCentre = selectedCentre === "" || data.centre === selectedCentre;
        const matchesCourse = selectedCourse === "" || data.course === selectedCourse;

        // issued date parse
        let issuedDate = null;
        if (data.issued) {
            // assuming format DD/MM/YYYY (based on your sample) 
            issuedDate = moment(data.issued, "DD/MM/YYYY");
        }

        const isAfterStart = startDate ? (issuedDate ? issuedDate.isSameOrAfter(moment(startDate)) : false) : true;
        const isBeforeEnd = endDate ? (issuedDate ? issuedDate.isSameOrBefore(moment(endDate)) : false) : true;

        return matchesSearch && matchesCentre && matchesCourse && isAfterStart && isBeforeEnd;
    });

    // Pagination logic
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredCertificates.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredCertificates.length / rowsPerPage);

    const handleChangePage = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setCurrentPage(newPage);
    };

    // Excel export
    const handleExportExcel = () => {
        const exportData = filteredCertificates.map(({ data }) => ({
            CandidateID: data.candidateId,
            Name: data.candidateName,
            Centre: data.centre,
            CertificateNo: data.certificateNo,
            Course: data.course,
            StartDate: data.start,
            EndDate: data.end,
            IssuedDate: data.issued,
            Timestamp: data.timestamp ? (data.timestamp.toDate ? data.timestamp.toDate() : data.timestamp) : ""
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Certificates");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
        saveAs(blob, `Certificates_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    };

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between mb-3">
                <a></a>
                {/* <Link to="/">
                    <img src="/images/menubutton.png" alt="Menu" style={{ height: "2rem" }} />
                </Link> */}
                <h5>Certificates Issued: (Filtered: {filteredCertificates.length} / Total: {certificates.length})</h5>
            </div>

            {/* Filters */}
            <div className="row mb-4 g-2">
                <div className="col-md-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by any field..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>

                <div className="col-md-2">
                    <select
                        className="form-select"
                        value={selectedCentre}
                        onChange={(e) => { setSelectedCentre(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">All Centres</option>
                        {uniqueCentres.map((centre, idx) => (
                            <option key={idx} value={centre}>{centre}</option>
                        ))}
                    </select>
                </div>

                <div className="col-md-2">
                    <select
                        className="form-select"
                        value={selectedCourse}
                        onChange={(e) => { setSelectedCourse(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">All Courses</option>
                        {uniqueCourses.map((course, idx) => (
                            <option key={idx} value={course}>{course}</option>
                        ))}
                    </select>
                </div>

                {/* <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                        placeholder="From Date"
                    />
                </div>

                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                        placeholder="To Date"
                    />
                </div> */}

                <div className="col-md-1">
                    <button
                        className="btn btn-secondary w-100"
                        onClick={() => {
                            setSearchTerm("");
                            setSelectedCentre("");
                            setSelectedCourse("");
                            setStartDate("");
                            setEndDate("");
                            setCurrentPage(1);
                        }}
                    >Reset</button>
                </div>


                <div className="col-md-1">
                    <div className="mb-2">
                        <button className="btn btn-success" onClick={handleExportExcel}>
                            Download Excel
                        </button>
                    </div>
                </div>


            </div>

            {/* Table */}
            <div className="table-responsive">
                <table className="table table-bordered table-hover" ref={tableRef}>
                    <thead className="thead-dark">
                        <tr>
                            <th>Sr. No</th>
                            <th>Candidate ID</th>
                            <th>Name</th>
                            <th>Centre</th>
                            <th>Certificate No</th>
                            <th>Course</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Issued Date</th>
                            <th>Timestamp</th>
                            <th>View Certificate</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRows.map(({ id, data }, idx) => (
                            <tr key={id}>
                                <td>{indexOfFirstRow + idx + 1}</td>
                                <td>{data.candidateId}</td>
                                <td>{data.candidateName}</td>
                                <td>{data.centre}</td>
                                <td>{data.certificateNo}</td>
                                <td>{data.course}</td>
                                <td>{data.start}</td>
                                <td>{data.end}</td>
                                <td>{data.issued}</td>
                                <td>
                                    {data.timestamp
                                        ? moment(
                                            data.timestamp.toDate
                                                ? data.timestamp.toDate()
                                                : data.timestamp
                                        ).format("DD/MM/YYYY, h:mm A")
                                        : "N/A"}
                                </td>
                                <td>
                                    <a
                                        href={`/view_certificate.html?id=${id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-info btn-sm text-white"
                                        style={{ display: "flex", justifyContent: "center", alignItems: "anchor-center", gap: "8px" }}
                                    >
                                        <i className="fa fa-eye"></i> View
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination controls */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Rows per page:
                    <select
                        className="form-select d-inline-block w-auto ms-2"
                        value={rowsPerPage}
                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        {[5, 10, 20, 50, 100, 500].map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <button
                        className="btn btn-outline-secondary me-2"
                        onClick={() => handleChangePage(currentPage - 1)}
                        disabled={currentPage === 1}
                    >Previous</button>
                    <span> Page {currentPage} of {totalPages} </span>
                    <button
                        className="btn btn-outline-secondary ms-2"
                        onClick={() => handleChangePage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >Next</button>
                </div>
            </div>
        </div>
    );
}

export default AtosCertificateList;



// import React, { useState, useEffect } from "react";
// import { useNavigate, Link } from 'react-router-dom';
// import db from "./Firebase";
// import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
// import Moment from 'moment';
// import { extendMoment } from 'moment-range';

// function AtosCertificateList() {
//     const moment = extendMoment(Moment);
//     const [certificates, setCertificates] = useState([]);
//     const [searchTerm, setSearchTerm] = useState("");
//     const [selectedCentre, setSelectedCentre] = useState("");
//     const [selectedCourse, setSelectedCourse] = useState("");
//     const [startDate, setStartDate] = useState("");
//     const [endDate, setEndDate] = useState("");
//     const navigate = useNavigate();
//     const [email, setEmail] = useState("");

//     useEffect(() => {
//         const storedUsername = localStorage.getItem("email");
//         const storedPassword = localStorage.getItem("password");
//         const storedRemember = localStorage.getItem("remember");

//         setEmail(storedUsername);

//         if (storedRemember === "false" || !storedUsername || !storedPassword) {
//             navigate('/signin');
//         }
//     }, []);

//     useEffect(() => {
//         const q = query(collection(db, "atos_certificates_data"), orderBy("timestamp", "desc"));
//         const unsubscribe = onSnapshot(q, (snapshot) => {
//             setCertificates(snapshot.docs.map(doc => ({
//                 id: doc.id,
//                 data: doc.data(),
//             })));
//         });

//         return () => unsubscribe();
//     }, []);

//     // Extract unique centres & courses for dropdowns
//     const uniqueCentres = [...new Set(certificates.map(({ data }) => data.centre))].filter(Boolean);
//     const uniqueCourses = [...new Set(certificates.map(({ data }) => data.course))].filter(Boolean);

//     // Combined Filter Logic
//     const filteredCertificates = certificates.filter(({ data }) => {
//         const searchableString = `
//             ${data.candidateId || ""}
//             ${data.candidateName || ""}
//             ${data.centre || ""}
//             ${data.certificateNo || ""}
//             ${data.course || ""}
//             ${data.start || ""}
//             ${data.end || ""}
//             ${data.issued || ""}
//         `.toLowerCase();

//         const matchesSearch = searchableString.includes(searchTerm.toLowerCase());
//         const matchesCentre = selectedCentre === "" || data.centre === selectedCentre;
//         const matchesCourse = selectedCourse === "" || data.course === selectedCourse;

//         const issuedDate = moment(data.issued, "DD/MM/YYYY");
//         const isAfterStart = startDate ? issuedDate.isSameOrAfter(moment(startDate)) : true;
//         const isBeforeEnd = endDate ? issuedDate.isSameOrBefore(moment(endDate)) : true;

//         return matchesSearch && matchesCentre && matchesCourse && isAfterStart && isBeforeEnd;
//     });

//     return (
//         <div className="p-3">
//             <div className="d-flex justify-content-between mb-3">
//                 <Link to="/">
//                     <img src="/images/menubutton.png" alt="Menu" style={{ height: "2rem" }} />
//                 </Link>
//                 <h5>Certificates Issued (Filtered: {filteredCertificates.length} / Total: {certificates.length})</h5>
//             </div>

//             {/* Filters */}
//             <div className="row mb-4 g-2">
//                 <div className="col-md-3">
//                     <input
//                         type="text"
//                         className="form-control"
//                         placeholder="Search by any field..."
//                         value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                     />
//                 </div>

//                 <div className="col-md-2">
//                     <select className="form-select" value={selectedCentre} onChange={(e) => setSelectedCentre(e.target.value)}>
//                         <option value="">All Centres</option>
//                         {uniqueCentres.map((centre, idx) => (
//                             <option key={idx} value={centre}>{centre}</option>
//                         ))}
//                     </select>
//                 </div>

//                 <div className="col-md-2">
//                     <select className="form-select" value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)}>
//                         <option value="">All Courses</option>
//                         {uniqueCourses.map((course, idx) => (
//                             <option key={idx} value={course}>{course}</option>
//                         ))}
//                     </select>
//                 </div>
//                 {/*
//                 <div className="col-md-2">
//                     <input
//                         type="date"
//                         className="form-control"
//                         value={startDate}
//                         onChange={(e) => setStartDate(e.target.value)}
//                         placeholder="From Date"
//                     />
//                 </div> */}

//                 {/* <div className="col-md-2">
//                     <input
//                         type="date"
//                         className="form-control"
//                         value={endDate}
//                         onChange={(e) => setEndDate(e.target.value)}
//                         placeholder="To Date"
//                     />
//                 </div> */}

//                 <div className="col-md-1">
//                     <button
//                         className="btn btn-secondary w-100"
//                         onClick={() => {
//                             setSearchTerm("");
//                             setSelectedCentre("");
//                             setSelectedCourse("");
//                             setStartDate("");
//                             setEndDate("");
//                         }}
//                     >
//                         Reset
//                     </button>
//                 </div>
//             </div>

//             {/* Table */}
//             <div className="table-responsive">
//                 <table className="table table-bordered table-hover">
//                     <thead className="thead-dark">
//                         <tr>
//                             <th>Sr. No</th>
//                             <th>Candidate ID</th>
//                             <th>Name</th>
//                             <th>Centre</th>
//                             <th>Certificate No</th>
//                             <th>Course</th>
//                             <th>Start Date</th>
//                             <th>End Date</th>
//                             <th>Issued Date</th>
//                             <th>Timestamp</th>
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {filteredCertificates.map(({ id, data }, index) => (
//                             <tr key={id}>
//                                 <td>{index + 1}</td>
//                                 <td>{data.candidateId}</td>
//                                 <td>{data.candidateName}</td>
//                                 <td>{data.centre}</td>
//                                 <td>{data.certificateNo}</td>
//                                 <td>{data.course}</td>
//                                 <td>{data.start}</td>
//                                 <td>{data.end}</td>
//                                 <td>{data.issued}</td>
//                                 <td>
//                                     {data.timestamp
//                                         ? moment(
//                                             data.timestamp.toDate
//                                                 ? data.timestamp.toDate()
//                                                 : data.timestamp
//                                         ).format("DD/MM/YYYY, h:mm A")
//                                         : "N/A"}
//                                 </td>
//                             </tr>
//                         ))}
//                     </tbody>
//                 </table>
//             </div>
//         </div>
//     );
// }

// export default AtosCertificateList;


// // import React, { useState, useEffect } from "react";
// // import { useNavigate, Link } from 'react-router-dom';
// // import Swal from 'sweetalert2';
// // import db from "./Firebase";
// // import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
// // import Moment from 'moment';
// // import { extendMoment } from 'moment-range';

// // function AtosCertificateList() {
// //     const moment = extendMoment(Moment);
// //     const [certificates, setCertificates] = useState([]);
// //     const [searchTerm, setSearchTerm] = useState("");
// //     const navigate = useNavigate();
// //     const [email, setEmail] = useState("");

// //     useEffect(() => {
// //         const storedUsername = localStorage.getItem("email");
// //         const storedPassword = localStorage.getItem("password");
// //         const storedRemember = localStorage.getItem("remember");

// //         setEmail(storedUsername);

// //         if (storedRemember === "false" || !storedUsername || !storedPassword) {
// //             navigate('/signin');
// //         }
// //     }, []);

// //     useEffect(() => {
// //         const q = query(collection(db, "atos_certificates_data"), orderBy("timestamp", "desc"));
// //         const unsubscribe = onSnapshot(q, (snapshot) => {
// //             setCertificates(snapshot.docs.map(doc => ({
// //                 id: doc.id,
// //                 data: doc.data(),
// //             })));
// //         });

// //         return () => unsubscribe();
// //     }, []);

// //     // 🔍 Flexible filter across all key fields
// //     const filteredCertificates = certificates.filter(({ data }) => {
// //         const searchableString = `
// //             ${data.candidateId || ""}
// //             ${data.candidateName || ""}
// //             ${data.centre || ""}
// //             ${data.certificateNo || ""}
// //             ${data.course || ""}
// //             ${data.start || ""}
// //             ${data.end || ""}
// //             ${data.issued || ""}
// //         `.toLowerCase();

// //         return searchableString.includes(searchTerm.toLowerCase());
// //     });

// //     return (
// //         <div className="p-3">
// //             <div className="d-flex justify-content-between mb-3">
// //                 <Link to="/">
// //                     <img src="/images/menubutton.png" alt="Menu" style={{ height: "2rem" }} />
// //                 </Link>
// //                 <h5>Certificates Issued (Count: {filteredCertificates.length})</h5>
// //             </div>

// //             <div className="mb-3">
// //                 <input
// //                     type="text"
// //                     className="form-control"
// //                     placeholder="Search by any field..."
// //                     value={searchTerm}
// //                     onChange={(e) => setSearchTerm(e.target.value)}
// //                     style={{ width: "300px" }}
// //                 />
// //             </div>

// //             <div className="table-responsive">
// //                 <table className="table table-bordered table-hover">
// //                     <thead className="thead-dark">
// //                         <tr>
// //                             <th>Sr. No</th>
// //                             <th>Candidate ID</th>
// //                             <th>Name</th>
// //                             <th>Centre</th>
// //                             <th>Certificate No</th>
// //                             <th>Course</th>
// //                             <th>Start Date</th>
// //                             <th>End Date</th>
// //                             <th>Issued Date</th>
// //                             <th>Timestamp</th>
// //                         </tr>
// //                     </thead>
// //                     <tbody>
// //                         {filteredCertificates.map(({ id, data }, index) => (
// //                             <tr key={id}>
// //                                 <td>{index + 1}</td>
// //                                 <td>{data.candidateId}</td>
// //                                 <td>{data.candidateName}</td>
// //                                 <td>{data.centre}</td>
// //                                 <td>{data.certificateNo}</td>
// //                                 <td>{data.course}</td>
// //                                 <td>{data.start}</td>
// //                                 <td>{data.end}</td>
// //                                 <td>{data.issued}</td>
// //                                 <td>
// //                                     {data.timestamp
// //                                         ? moment(
// //                                             data.timestamp.toDate
// //                                                 ? data.timestamp.toDate()
// //                                                 : data.timestamp
// //                                         ).format("DD/MM/YYYY, h:mm A")
// //                                         : "N/A"}
// //                                 </td>
// //                             </tr>
// //                         ))}
// //                     </tbody>
// //                 </table>
// //             </div>
// //         </div>
// //     );
// // }

// // export default AtosCertificateList;
